import { useState } from "react";
import { useGetIdentity, useListContext, useNotify, useRefresh } from "ra-core";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOffers, type Offer } from "../useOffers";
import { useSequences, type Sequence } from "../sequences/enroll";
import { promoteMany, promoteMessage } from "./promote";
import { HelpTooltip } from "../help/HelpTooltip";
import {
  DAILY_CAP_MESSAGE,
  NO_OFFERS_MESSAGE,
  usePromotionsToday,
} from "../freelancerNotices";

/**
 * Botón de la barra de selección del explorador: pasa las empresas elegidas
 * al CRM con promote_directory() y abre una oportunidad en la oferta elegida.
 */
export const PromoteButton = () => {
  const { selectedIds, filterValues, onUnselectItems } = useListContext();
  const { data: offers } = useOffers();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // paso 2: oferta elegida que tiene cadencias → preguntar si inscribir
  const [offer, setOffer] = useState<Offer | null>(null);
  const { data: sequences } = useSequences();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // E6.6: tope diario del freelancer (el dueño no tiene: tope null)
  const { data: today } = usePromotionsToday();
  const cap = today?.tope ?? null;
  const capReached = cap !== null && today!.usadas >= cap;

  // Si el explorador está filtrado por oferta, esa se sugiere primero
  const filteredSlug = String(filterValues?.["fits@cs"] ?? "").replace(
    /[{}]/g,
    "",
  );

  const chooseOffer = (o: Offer) => {
    if (sequences?.some((s) => s.offer_id === o.id)) setOffer(o);
    else promote(o, null);
  };

  const promote = async (offer: Offer, sequence: Sequence | null) => {
    setBusy(true);
    const r = await promoteMany(
      selectedIds,
      offer,
      sequence,
      Number(identity?.id),
    );
    setBusy(false);
    queryClient.invalidateQueries({ queryKey: ["promotions_today"] });
    setOpen(false);
    setOffer(null);
    onUnselectItems();
    refresh();
    const [msg, type] = promoteMessage(r, offer.name, sequence?.name);
    notify(msg, { type });
    // Una sola empresa: abrir su ficha
    if (selectedIds.length === 1 && r.companyIds.length === 1) {
      navigate(`/companies/${r.companyIds[0]}/show`);
    }
  };

  const sorted = [...(offers ?? [])].sort(
    (a, b) =>
      Number(b.slug === filteredSlug) - Number(a.slug === filteredSlug) ||
      a.priority - b.priority,
  );

  return (
    <>
      <div className="inline-flex flex-wrap items-center gap-1">
        <Button size="sm" onClick={() => setOpen(true)} disabled={capReached}>
          <ArrowUpRight />
          Pasar al CRM
        </Button>
        <HelpTooltip topic="directory.promote" />
        {cap !== null && (
          <span className="text-xs text-muted-foreground">
            Hoy: {today!.usadas} de {cap}
          </span>
        )}
        {capReached && (
          <span className="text-xs text-destructive">{DAILY_CAP_MESSAGE}</span>
        )}
      </div>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (busy) return;
          setOpen(o);
          setOffer(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          {offer ? (
            <>
              <DialogHeader>
                <DialogTitle>¿Inscribir en cadencia?</DialogTitle>
                <DialogDescription>
                  Sus tareas aparecerán en Mi día desde hoy.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                {sequences
                  ?.filter((s) => s.offer_id === offer.id)
                  .map((s) => (
                    <Button
                      key={s.id}
                      className="h-12 text-base"
                      disabled={busy}
                      onClick={() => promote(offer, s)}
                    >
                      {s.name}
                    </Button>
                  ))}
                <Button
                  variant="outline"
                  className="h-12 text-base"
                  disabled={busy}
                  onClick={() => promote(offer, null)}
                >
                  No, solo pasar al CRM
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>¿Para qué oferta?</DialogTitle>
                <DialogDescription>
                  {selectedIds.length === 1
                    ? "Se crea la empresa en el CRM y se abre la oportunidad."
                    : `${selectedIds.length} empresas: se crean en el CRM y se abre una oportunidad a cada una.`}
                </DialogDescription>
              </DialogHeader>
              {offers?.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {NO_OFFERS_MESSAGE}
                </p>
              )}
              <div className="grid gap-2">
                {sorted.map((offer) => (
                  <Button
                    key={offer.id}
                    variant={
                      offer.slug === filteredSlug ? "default" : "outline"
                    }
                    className="h-12 justify-between text-base"
                    disabled={busy}
                    onClick={() => chooseOffer(offer)}
                  >
                    {offer.name}
                    <span className="text-xs opacity-70">
                      prioridad {offer.priority}
                    </span>
                  </Button>
                ))}
              </div>
            </>
          )}
          {busy && (
            <p className="text-sm text-muted-foreground">Pasando al CRM…</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
